import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from 'react';
import { FailedToGetData, FloatingSaveBar, H2, HorizontalSelector, Loading, RadioSelector } from 'src/components/_commons';
import { generalHelpers, months } from '@monorepo/helpers';
import { hideLoadingModalReducer, setAlertResponse, setValuesAndOpenAlertModalReducer, showLoadingModalReducer } from 'src/store';
import { houseToHouseServices, publicMinistryServices } from 'src/services';
import { HTHActivityDay } from './HTHActivity1Day';
import { HTHActivityService } from './HTHActivity2Service';
import { typeHTHTerritory, typePublicMinistryMonthlyService } from '@monorepo/models';
import { useDispatch } from 'react-redux';
import { useGetIsDarkMode } from 'src/custom-hooks';

type propsType = {
    hthTerritories: typeHTHTerritory[];
    setHthTerritories: Dispatch<SetStateAction<typeHTHTerritory[] | null>>;
};

export const HTHActivity: FC<propsType> = ({ hthTerritories}) => {
    const [isLoading, setIsLoading] = useState(true);
    const [monthlyServices, setMonthlyServices] = useState<typePublicMinistryMonthlyService[] | null>(null);
    const [radioValue, setRadioValue] = useState<'byDay' | 'byService'>('byDay');
    const [refreshCounter, setRefreshCounter] = useState(0);
    const [refreshCounterLocal, setRefreshCounterLocal] = useState(0);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [territories, setTerritories] = useState<typeHTHTerritory[] | null>(null);
    const dispatch = useDispatch();
    const isDarkMode = useGetIsDarkMode();

    const monthlyService: typePublicMinistryMonthlyService | null = useMemo(() =>
        monthlyServices?.find(ms => ms.month === selectedDate.getMonth() && ms.year === selectedDate.getFullYear()) ?? null
    , [monthlyServices, selectedDate]);

    const modifiedTerritories: typeHTHTerritory[] = useMemo(() =>
        territories?.filter(t => t.faces.some(p =>
            JSON.stringify(p.serviceDates) !== JSON.stringify(hthTerritories.find(_t => _t.number === t.number)!.faces.find(_p => _p.id === p.id)?.serviceDates)
        )) ?? []
    , [hthTerritories, territories]);

    const saveHandler = () => {
        if (!monthlyService) {
            return;
        }
        dispatch(setValuesAndOpenAlertModalReducer({
            title: 'Confirmar',
            message: `Se van a guardar los cambios hechos en los territorios: ${modifiedTerritories.sort((t1, t2) => t1.number - t2.number).map(t => t.number).join(', ')}`,
            mode: 'confirm',
            execution: async () => {
                dispatch(showLoadingModalReducer());
                const response = await houseToHouseServices.SetActivity(modifiedTerritories, monthlyService.month, monthlyService.year);
                dispatch(hideLoadingModalReducer());
                if (!response.success) {
                    dispatch(setAlertResponse({ response }));
                    return;
                }
                setRefreshCounter(x => x + 1);
            }
        }));
    }

    const cancelHandler = () => {
        dispatch(setValuesAndOpenAlertModalReducer({
            title: '¿Cancelar?',
            message: `Se van a perder los cambios hechos en los territorios: ${modifiedTerritories.sort((t1, t2) => t1.number - t2.number).map(t => t.number).join(', ')}`,
            animation: 3,
            mode: 'confirm',
            execution: () => setRefreshCounterLocal(x => x + 1)
        }));
    }

    useEffect(() => {
        publicMinistryServices.Get().then(response => {
            setIsLoading(false);
            if (!response.success || !response.data?.publicMinistryDoc) {
                dispatch(setAlertResponse({ response }));
                return;
            }
            const justHth = response.data.publicMinistryDoc.monthlyServices.map(ms => ({
                ...ms,
                services: ms.services.filter(s => s.typeOfPreaching === 'house-to-house')
            }));
            setMonthlyServices(justHth);
        });
    }, [refreshCounter]);

    useEffect(() => {
        setTerritories(
            hthTerritories.map(t => ({
                ...t,
                zoom: t.zoom - 0.5
            }))
        );
    }, [hthTerritories, refreshCounterLocal]);

    if (isLoading) {
        return <Loading mt={'80px'} />;
    }

    if (!monthlyServices || !territories) {
        return <FailedToGetData />;
    }

    return (
        <>
        <H2 title={'ACTIVIDAD'} />
        <HorizontalSelector
            label={`${months[selectedDate.getMonth()].toUpperCase()} ${selectedDate.getFullYear()}`}
            setNext={() => {
                if (modifiedTerritories.length) {
                    return;
                }
                const nextMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 1);
                setSelectedDate(nextMonth);
            }}
            setPrevious={() => {
                if (modifiedTerritories.length) {
                    return;
                }
                const prevMonth = new Date(selectedDate.getFullYear(), selectedDate.getMonth() - 1, 1);
                setSelectedDate(x => prevMonth);
            }}
        />
        <RadioSelector
            options={[
                { label: radioValue === 'byDay' ? 'Viendo por Día' : 'Ver por Día', type: 'byDay' },
                { label: radioValue === 'byService' ? 'Viendo por Asignación' : 'Ver por Asignación', type: 'byService' },
            ]}
            radioValue={radioValue}
            setRadioValue={setRadioValue}
        />
        {monthlyService &&
            <div className={`${isDarkMode ? 'text-white' : ''} mt-5`}>
                {radioValue === 'byDay' ?
                    <>
                    {generalHelpers.GetPublicMinistryServicesByDay(monthlyService)
                     .map(d => ({ ...d, services: d.services.map(s => ({ ...s, weekday: new Date(monthlyService.year, monthlyService.month, d.day!).getDay() })) }))
                     .map(d =>
                        <HTHActivityDay key={`${monthlyService.month}_${d.day}`}
                            day={d.day}
                            hthTerritories={territories}
                            monthlyService={monthlyService}
                            services={d.services}
                            setHthTerritories={setTerritories}
                            setRefreshCounter={setRefreshCounter}
                        />
                    )}
                    </>
                    :
                    <>
                    {monthlyService.services
                     .filter(s => s.typeOfRegularity <= 2)
                     .sort((s1, s2) => ((s1.weekday || 7) - (s2.weekday || 7)) || (s1.hour - s2.hour) || (s1.minutes - s2.minutes))
                     .map(s =>
                        <HTHActivityService key={s.id}
                            completionDate={new Date(monthlyService.year, monthlyService.month + 1, 0).getTime()}
                            hthTerritories={territories}
                            monthlyService={monthlyService}
                            service={s}
                            services={[s]}
                            setHthTerritories={setTerritories}
                            setRefreshCounter={setRefreshCounter}
                        />
                    )}
                    {monthlyService.services
                     .filter(s => s.typeOfRegularity >= 3)
                     .sort((s1, s2) => (s1.day! - s2.day!) || (s1.hour - s2.hour) || (s1.minutes - s2.minutes))
                     .map(s => ({ ...s, weekday: new Date(monthlyService.year, monthlyService.month, s.day!).getDay() }))
                     .map(s =>
                        <HTHActivityService key={s.id}
                            completionDate={new Date(monthlyService.year, monthlyService.month + 1, 0).getTime()}
                            hthTerritories={territories}
                            monthlyService={monthlyService}
                            service={s}
                            services={[s]}
                            setHthTerritories={setTerritories}
                            setRefreshCounter={setRefreshCounter}
                        />
                    )}
                    </>
                }
                {!!modifiedTerritories.length &&
                    <FloatingSaveBar
                        cancelHandler={cancelHandler}
                        isDisabledSaveButton={false}
                        saveHandler={saveHandler}
                    />
                }
            </div>
        }
        </>
    );
}
